import { Fragment } from 'react';

import { CodeSpan } from '../CodeSpan';
import { getAnalysisTypeDisplayName } from '../getAnalysisTypeDisplayName';
import { getSuccessCriteriaDisplayName } from '../getSuccessCriteriaDisplayName';
import { RandomizationUnitsSection } from '../RandomizationUnitsSection';
import type { MeasureType, Metric } from '../types';

import styles from './MetricDefinition.module.css';

interface MetricDefinitionProps {
  metric: Metric;
  className?: string;
  /**
   * Optional list of randomization units that are actually in use.
   * When provided, only these randomization units will be shown,
   * rather than all randomization units the metric supports.
   */
  randomizationUnitsInUse?: string[];
}

type AggregationSentencePrefix = 'Count' | 'Percent' | 'Sum' | 'Average';

function getAggregationSentencePrefix(metric: Metric): AggregationSentencePrefix | null {
  if (metric.kind !== 'custom') {
    return null;
  }
  if (metric.isNumeric) {
    if (metric.unitAggregationType === 'sum') {
      return 'Sum';
    }
    if (metric.unitAggregationType === 'average') {
      return 'Average';
    }
    return null;
  }
  if (metric.unitAggregationType === 'sum') {
    return 'Count';
  }
  if (metric.unitAggregationType === 'average') {
    return 'Percent';
  }
  return null;
}

/**
 * Returns true when MetricDefinition renders the aggregation-driven sentence
 * that includes the success criteria inline. Callers (e.g. MetricConfiguration)
 * can use this to avoid showing a duplicate "Success criteria" row.
 */
export function metricDefinitionIncludesSuccessCriteria(metric: Metric): boolean {
  return getAggregationSentencePrefix(metric) !== null;
}

const RandomizationUnitsList: React.FC<{ units: string[] }> = ({ units }) => {
  if (units.length === 0) {
    return null;
  }
  return (
    <>
      {units.map((unit, index) => (
        <Fragment key={unit}>
          {index > 0 && (index === units.length - 1 ? ' or ' : ', ')}
          <CodeSpan textValue={unit} />
        </Fragment>
      ))}
    </>
  );
};

export const MetricDefinition: React.FC<MetricDefinitionProps> = ({ metric, className, randomizationUnitsInUse }) => {
  const measureType: MeasureType = metric.isNumeric
    ? 'value'
    : metric.unitAggregationType === 'sum'
      ? 'count'
      : 'occurrence';
  const analysisTypeString = getAnalysisTypeDisplayName(metric.analysisType, metric.percentileValue);

  // Filter randomization units to only show those in use, if specified
  const metricRandomizationUnits = metric.randomizationUnits ?? [];
  const filteredRandomizationUnits = randomizationUnitsInUse
    ? metricRandomizationUnits.filter((unit) => randomizationUnitsInUse.includes(unit))
    : metricRandomizationUnits;
  const randomizationUnitsToShow =
    filteredRandomizationUnits.length > 0 ? filteredRandomizationUnits : metricRandomizationUnits;

  const aggregationSentencePrefix = getAggregationSentencePrefix(metric);

  if (aggregationSentencePrefix) {
    // Legacy metrics may have an unset successCriteria; fall back to the system
    // default ('HigherThanBaseline') so the sentence stays readable and matches
    // what the picker would have shown.
    const successCriteriaNode = (
      <CodeSpan textValue={getSuccessCriteriaDisplayName(metric.successCriteria ?? 'HigherThanBaseline')} />
    );

    const hasUnits = randomizationUnitsToShow.length > 0;

    if (aggregationSentencePrefix === 'Percent') {
      return (
        <span className={className} id="metricDefinition">
          <CodeSpan textValue="Percent" className={styles.codeSpanWithMargin} /> of{' '}
          {hasUnits && <RandomizationUnitsList units={randomizationUnitsToShow} />}
          {hasUnits ? ' units that sent the event where ' : 'units that sent the event where '}
          {successCriteriaNode}
        </span>
      );
    }

    const eventNoun = aggregationSentencePrefix === 'Count' ? 'events' : 'event values';
    return (
      <span className={className} id="metricDefinition">
        <CodeSpan textValue={aggregationSentencePrefix} className={styles.codeSpanWithMargin} />{' '}
        {hasUnits ? `${eventNoun} per ` : `${eventNoun}`}
        {hasUnits && <RandomizationUnitsList units={randomizationUnitsToShow} />}, then compute the{' '}
        <CodeSpan textValue={analysisTypeString} />
        {hasUnits && ' of those values'} where {successCriteriaNode}
      </span>
    );
  }

  return (
    <span className={className} id="metricDefinition">
      {measureType !== 'occurrence' && (
        <>
          <CodeSpan textValue={analysisTypeString} className={styles.codeSpanWithMargin} />{' '}
        </>
      )}
      <RandomizationUnitsSection
        measureType={measureType}
        unitAggregationType={metric.unitAggregationType}
        metricKind={metric.kind}
        readonly
        readonlyValues={randomizationUnitsToShow}
        omitWhereConnector
      />
    </span>
  );
};
